/**
 * useMigrationUpload.ts
 * Mirrors useConfigUpload.ts pattern.
 * Calls POST /api/data-migration/upload with a single SPP Excel file.
 */

import { useState, useCallback } from 'react';
import useSession from '@hooks/useSession';
import { getRuntimeEnv } from '@utils/runtimeEnv';
import { appInsights } from '../appInsights';

const API_BASE =
  getRuntimeEnv('REACT_APP_OATT_API_BASE') ||
  `${getRuntimeEnv('REACT_APP_SERVER_PROTOCOL')}://${getRuntimeEnv('REACT_APP_SERVER_HOST')}/api`;

// ── Types ──────────────────────────────────────────────────────────────────

export interface MigrationSkippedBreakdown {
  hidden: string[];
  accrual: string[];
  emptyOrUnrecognised: string[];
  readError: string[];
  parseError: string[];
}

export interface MigrationStats {
  totalSheets: number;
  sheetsProcessed: number;
  sheetsSkipped: number;
  sheetsSkippedBreakdown: MigrationSkippedBreakdown;
  totalRowsRead: number;
  validRowsInserted: number;
  invalidRowsSkipped: number;
  consolidatedRecordsInserted?: number;
  errorCount: number;
}

export interface MigrationUploadResult {
  success: boolean;
  data?: {
    migrationBatchId: string;
    stats: MigrationStats;
  };
  error?: string;
}

export interface MigrationHistoryItem {
  id: number;
  migration_batch_id: string;
  file_name: string;
  uploaded_by: string;
  uploaded_at: string;
  status: string;
  valid_rows_inserted: number;
  vendor_records_inserted: number;
  consolidated_records_inserted: number;
  invalid_rows_skipped: number;
  has_errors: boolean;
}

interface MigrationUploadState {
  uploading: boolean;
  result: MigrationUploadResult | null;
  error: string | null;
}

// ── Hook ──────────────────────────────────────────────────────────────────

export function useMigrationUpload() {
  const { accessToken } = useSession();

  const [state, setState] = useState<MigrationUploadState>({
    uploading: false,
    result: null,
    error: null,
  });

  const uploadFile = useCallback(
    async (file: File): Promise<boolean> => {
      if (!file) {
        setState((s) => ({ ...s, error: 'No file selected.' }));
        return false;
      }

      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!['xlsx', 'xls', 'xlsm'].includes(ext)) {
        setState((s) => ({ ...s, error: 'Only .xlsx, .xls, and .xlsm files are accepted.' }));
        return false;
      }

      setState({ uploading: true, result: null, error: null });

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_BASE}/data-migration/upload`, {
          method: 'POST',
          body: formData,
          headers: { Authorization: `Bearer ${accessToken}` },
          // Do NOT set Content-Type — browser sets multipart boundary automatically
        });

        const contentType = response.headers.get('content-type') ?? '';
        if (!contentType.includes('application/json')) {
          const text = await response.text();
          throw new Error(`Server error (${response.status}): ${text.slice(0, 300)}`);
        }

        const data: MigrationUploadResult = await response.json();

        if (!response.ok) {
          throw new Error(
            data.error || `Upload failed (${response.status})`
          );
        }

        setState({ uploading: false, result: data, error: null });
        if (data.success) {
          appInsights?.trackEvent({
            name: 'DataMigrationSuccess',
            properties: {
              fileName: file.name,
              migrationBatchId: data.data?.migrationBatchId ?? '',
              validRowsInserted: data.data?.stats?.validRowsInserted ?? 0,
            },
          });
        } else {
          appInsights?.trackEvent({
            name: 'DataMigrationFailure',
            properties: { fileName: file.name, error: data.error ?? '' },
          });
        }
        return data.success === true;
      } catch (err: any) {
        const msg = err?.message ?? 'Upload failed. Please try again.';
        setState({ uploading: false, result: null, error: msg });
        appInsights?.trackEvent({
          name: 'DataMigrationFailure',
          properties: { fileName: file.name, error: msg },
        });
        return false;
      }
    },
    [accessToken]
  );

  const downloadErrorReport = useCallback(
    async (batchId: string, fileName: string): Promise<void> => {
      try {
        const response = await fetch(
          `${API_BASE}/data-migration/${batchId}/errors/download`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (!response.ok) {
          throw new Error(`Download failed (${response.status})`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `migration_errors_${batchId}_${fileName}.xlsx`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
      } catch (err: any) {
        console.error('Error download failed:', err); // eslint-disable-line no-console
      }
    },
    [accessToken]
  );

  const fetchHistory = useCallback(
    async (page = 1, pageSize = 10): Promise<{ items: MigrationHistoryItem[]; total: number } | null> => {
      try {
        const response = await fetch(
          `${API_BASE}/data-migration?page=${page}&pageSize=${pageSize}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!response.ok) return null;
        const json = await response.json();
        return json.data ?? null;
      } catch {
        return null;
      }
    },
    [accessToken]
  );

  const reset = useCallback(() => {
    setState({ uploading: false, result: null, error: null });
  }, []);

  return { ...state, uploadFile, downloadErrorReport, fetchHistory, reset };
}
